import { BreakPointCondition, UI, useBreakpointIf, HStack, VStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { Box, Button } from 'native-base';
import React from 'react';
import { Pressable } from 'react-native';

import { getValidatedStatusIcon } from '../../../../../utils/getValidatedStatusIcon';
import { BasketLink } from '../../../../OrderHistoryModal/components/BasketLink';
import { messageError } from '../../constants';

import {
  OrderHistoryItemComponentProps,
  formatDate,
  formatMoney,
  handleExportNewOrder,
} from './constants';

interface ITableItemProps {
  label: string;
  value: string | number;
  valueUppercase?: boolean;
}

const TableItem = ({ label, value, valueUppercase }: ITableItemProps) => (
  <HStack justifyContent="space-between" space={Spacing['inline.quarck']}>
    <UI.Text color="neutral.medium" fontSize={Typography['fontsize.md']}>
      {label}
    </UI.Text>
    <UI.Text
      color="neutral.black"
      fontSize={Typography['fontsize.md']}
      textTransform={valueUppercase ? 'uppercase' : 'inherit'}>
      {`${value}`}
    </UI.Text>
  </HStack>
);

export const OrderHistoryItemMobile = ({
  item,
  onSelect,
  isReferencedByBasket,
  onBasketPress,
}: OrderHistoryItemComponentProps) => {
  const isBetweenTabletAndMobile = useBreakpointIf(BreakPointCondition.EqualOrUp, 'sm');

  const orderAmount = formatMoney(item?.requestedAmount);

  const sendOrderDate = formatDate(item?.orderDate, 'dd/MM/yyyy');

  const statusItem = getValidatedStatusIcon(item.orderStatus.statusIcon);

  return (
    <UI.Shadow
      testId="table-item-tablet"
      level={1}
      borderRadius="8px"
      marginY={isBetweenTabletAndMobile ? Spacing['stack.nano'] : '0'}>
      <VStack p={Spacing['inset.sm']} space={Spacing['stack.xxxs']}>
        <HStack alignItems="center" justifyContent="space-between">
          <HStack space={Spacing['inline.xxxs']}>
            <UI.Text variant="semibold" fontSize={Typography['fontsize.lg']}>
              {item.orderHash || messageError}
            </UI.Text>
            {isReferencedByBasket && <BasketLink onPress={onBasketPress} />}
          </HStack>
          <HStack alignItems="center" space={Spacing['inline.xxxs']}>
            <UI.IconStatus
              testId="icon-status"
              icon={statusItem.icon}
              color={statusItem.iconColor}
              bgColor={statusItem.backgroundColor}
              size={statusItem.iconSize}
            />
            <Pressable
              testID="order-history-button-icon"
              onPress={() => handleExportNewOrder(item)}>
              <UI.Icon name="Pdf" size="xs" color={Color['brand.tertiary.principal']} />
            </Pressable>
          </HStack>
        </HStack>

        <TableItem label="Tipo" valueUppercase value={item?.sideDescription || '--'} />
        <Box w="full" h="1px" bgColor={Color['neutral.lightest']} />
        <TableItem label="Data de envio" value={sendOrderDate} />
        <Box w="full" h="1px" bgColor={Color['neutral.lightest']} />
        <TableItem label="Cliente" value={item.customer.customerName || '--'} />
        <Box w="full" h="1px" bgColor={Color['neutral.lightest']} />
        <TableItem label="Produto" value={item.marketType || '--'} valueUppercase />

        <HStack justifyContent="space-between" alignItems="center">
          <VStack space={Spacing['stack.quarck']}>
            <UI.Text variant="semibold" fontSize={Typography['fontsize.md']} color="neutral.medium">
              Total enviado
            </UI.Text>
            <UI.Text variant="semibold" fontSize={Typography['fontsize.lg']} color="neutral.black">
              {orderAmount}
            </UI.Text>
          </VStack>
          <Button
            color={Color['brand.tertiary.principal']}
            variant="textLink"
            onPress={() => onSelect(item)}
            rightIcon={
              <UI.Icon name="ArrowRight" size="xs" color={Color['brand.tertiary.principal']} />
            }
          />
        </HStack>
      </VStack>
    </UI.Shadow>
  );
};

export const OrderHistoryItemMobileSkeleton = () => {
  const isBetweenTabletAndMobile = useBreakpointIf(BreakPointCondition.EqualOrUp, 'sm');

  return (
    <UI.Shadow
      testId="table-item-tablet"
      level={1}
      borderRadius="8px"
      marginY={isBetweenTabletAndMobile ? Spacing['stack.nano'] : '0'}>
      <VStack p={Spacing['inset.sm']} space={Spacing['stack.xxxs']}>
        <HStack alignItems="center" space={Spacing['inline.xxs']} justifyContent="space-between">
          <UI.Text isLoading />
          <UI.Text isLoading />
        </HStack>
        <HStack space={Spacing['inline.xxxs']} justifyContent="space-between">
          <UI.Text isLoading />
          <UI.Text isLoading />
        </HStack>
        <Box w="full" h="1px" bgColor={Color['neutral.lightest']} />
        <HStack space={Spacing['inline.xxxs']} justifyContent="space-between">
          <UI.Text isLoading />
          <UI.Text isLoading />
        </HStack>
        <Box w="full" h="1px" bgColor={Color['neutral.lightest']} />
        <HStack justifyContent="space-between" alignItems="center">
          <VStack space={Spacing['stack.quarck']} w="1/3">
            <UI.Text isLoading />
            <UI.Text isLoading />
          </VStack>
          <Button
            color={Color['brand.tertiary.principal']}
            variant="textLink"
            isDisabled
            rightIcon={
              <UI.Icon name="ArrowRight" size="xs" color={Color['brand.tertiary.principal']} />
            }
          />
        </HStack>
      </VStack>
    </UI.Shadow>
  );
};

export default OrderHistoryItemMobile;
